import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { formatDate } from '@angular/common';
import { Injectable, signal } from '@angular/core';
import { ApolloLink } from '@apollo/client/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import groupBy from 'lodash-es/groupBy';
import { Observable, map, switchMap } from 'rxjs';
import { BypassErrorLink } from 'shared/abstract/bypass-error-link';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { GridService } from 'shared/interfaces/grid-service';
import { ApplicationPermissionArray, ResourcePermission, UserGroupPermission } from 'shared/interfaces/permissions.interface';
import { CachingService, UserApiService } from 'shared/services';
import { convertObjectArrayToString } from 'shared/util/convert-object-array-to-string';
import { UserGroup, UserPermission } from 'users/interfaces/user.interface';
import { VisualPermissionResponse } from 'users/interfaces/visual-permissions.interface';

@Injectable({
    providedIn: 'root',
})
export class UserGroupsService implements GridService {
    private apollo: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
        private userApiService: UserApiService,
    ) {
        this.apollo = this.apolloProvider.use('usersAPI');
    }

    NAME = 'UserGroupsGridService';

    userGroupHasVisualsUiEnabled = signal<boolean | null>(null);

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        return this.userApiService.getLoginInformation().pipe(
            switchMap((userData: CurrentSupplierUserInterface | CurrentShijiUserInterface) => {
                const supplierId = (userData as CurrentSupplierUserInterface)?.supplierId;

                const getUserGroups = `
                    query getUserGroups (
                        $companyName: String,
                        $groupName: String,
                        $createdAt: DateOnly,
                        $modifiedAt: DateOnly,
                        $orderDirection: OrderDirection,
                        $orderBy: String
                    ) {
                        userGroups {
                            search(
                                searchSettings: {
                                    pageNumber: ${pageNumber},
                                    pageSize: ${pageSize},
                                    orderDirection: $orderDirection,
                                    orderBy: $orderBy
                                },
                                filtering: {
                                    ${supplierId ? `supplierIds: ["${supplierId}"]` : ''},
                                    companyName: $companyName,
                                    name: $groupName,
                                    createdAt: $createdAt,
                                    modifiedAt: $modifiedAt
                                }
                            )  {
                                data {
                                    id,
                                    supplierId,
                                    name,
                                    isAdmin,
                                    entityTracking {
                                        createdBy,
                                        createdAt,
                                        modifiedBy,
                                        modifiedAt
                                    },
                                    ${!supplierId ? `, companyName` : ''},
                                    userAccesses {
                                        id
                                    }
                                },
                                totalCount
                            }
                        }
                    }
                `;

                return this.apollo
                    .query({
                        query: gql(getUserGroups),
                        variables: {
                            orderBy: params.request.sortModel[0]?.colId,
                            orderDirection: params.request.sortModel[0]?.sort.toUpperCase(),
                            companyName: (params.request.filterModel as FilterModel)['companyName']?.filter,
                            groupName: (params.request.filterModel as FilterModel)['name']?.filter,
                            createdAt: (params.request.filterModel as FilterModel)['createdAt']?.filter
                                ? formatDate((params.request.filterModel as FilterModel)['createdAt']?.filter, 'yyyy-MM-dd', 'en-US')
                                : undefined,
                            modifiedAt: (params.request.filterModel as FilterModel)['modifiedAt']?.filter
                                ? formatDate((params.request.filterModel as FilterModel)['modifiedAt']?.filter, 'yyyy-MM-dd', 'en-US')
                                : undefined,
                        },
                        fetchPolicy: 'no-cache',
                    })
                    .pipe(
                        map((response: any) => {
                            return response.data.userGroups.search;
                        }),
                    );
            }),
        );
    }

    public getPermissionsByUserGroupId(userGroupId: string): Observable<ApplicationPermissionArray> {
        return this.apollo
            .query({
                query: gql`
                query getUserGroupPermissions {
                    userGroups {
                        search(
                            searchSettings: {}
                            filtering: {
                                ids: ["${userGroupId}"]
                            }
                        ) {
                            data {
                                userGroupPermissions {
                                    id
                                    permissionAccessZone
                                    accessType
                                }
                                resourcePermissions {
                                    id
                                    userGroupId
                                    resourceId
                                    accessType
                                }
                                hasVisualsUIEnabled
                                visualPermissions {
                                    id
                                    userId
                                    userGroupId
                                    type
                                    accessType
                                    isImplemented
                                    derivedVisualPermissions {
                                        visualPermissionId
                                        type
                                        isActive
                                        isImplemented
                                        id
                                    }
                                }
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const hasVisualsUIEnabled = response.data.userGroups.search.data[0]?.hasVisualsUIEnabled;
                    this.userGroupHasVisualsUiEnabled.set(hasVisualsUIEnabled);
                    return response.data.userGroups.search.data as {
                        userGroupPermissions: UserGroupPermission[];
                        resourcePermissions: ResourcePermission[];
                        visualPermissions: VisualPermissionResponse[];
                    }[];
                }),
                map((data) => {
                    if (data === null || data.length === 0) {
                        return [[], [], []];
                    } else {
                        return [data[0].resourcePermissions, data[0].userGroupPermissions, data[0].visualPermissions];
                    }
                }),
            );
    }

    // User group update/create functions (they are all used)
    public createUserGroup({ supplierId, name, description }: { supplierId: string; name: string; description?: string }): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                    mutation userGroups {
                        userGroups {
                            createGroup(userGroup: {
                                supplierId: "${supplierId}",
                                name: "${name}",
                                description: "${description}"
                            }) {
                                data,
                                statusCode,
                                message
                            }
                        }
                    }
                `,
                context: {
                    link: ApolloLink.from([new BypassErrorLink([409])]),
                },
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroups.createGroup;
                }),
            );
    }

    public updateUserGroup(userGroup: { groupName: string; id: string; [key: string]: any }) {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation updateGroup {
                    userGroups {
                        updateGroup(userGroup: {
                            name: "${userGroup.groupName}"
                            id: "${userGroup.id}"
                        }) {
                            statusCode
                            message
                        }
                    }
                }`,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroups.updateGroup;
                }),
            );
    }

    public deleteUserGroupById(userGroupId: string) {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation deleteUserGroup {
                    userGroups {
                        deleteUserGroup(userGroupID: "${userGroupId}") {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroups.deleteUserGroup.data;
                }),
            );
    }

    public createUserGroupPermissions(userGroupPermissions: Partial<UserGroupPermission>[]): Observable<any> {
        const createParams = convertObjectArrayToString(
            userGroupPermissions,
            ['id', 'userGroupId', 'permissionAccessZone', 'accessType'],
            ['permissionAccessZone', 'accessType'],
        );

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userGroupPermissions {
                    userGroupPermissions {
                        createMultipleGroupPermission(userGroupPermissions: ${createParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroupPermissions.createMultipleGroupPermission.data;
                }),
            );
    }

    public createUserPermissions(userPermissions: Partial<UserPermission>[]): Observable<any> {
        const createParams = convertObjectArrayToString(
            userPermissions,
            ['id', 'userId', 'permissionAccessZone', 'accessType'],
            ['permissionAccessZone', 'accessType'],
        );

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userPermissions {
                    userPermissions {
                        createMultiplePermissions(userPermissions: ${createParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userPermissions.createMultiplePermissions.data;
                }),
            );
    }

    public createUserGroupResourcePermissions(resourcePermissions: Partial<ResourcePermission>[]): Observable<any> {
        const createParams = convertObjectArrayToString(resourcePermissions, undefined, ['permissionAccessZone', 'accessType']).replace(
            '"null"',
            'null',
        );

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userGroupResourcePermissions {
                    resourcePermission {
                        createMultipleGroupPermissions(resourcePermissions: ${createParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.resourcePermission.createMultipleGroupPermissions.data;
                }),
            );
    }

    public createResourcePermissions(resourcePermissions: Partial<ResourcePermission>[]): Observable<any> {
        const createParams = convertObjectArrayToString(resourcePermissions, undefined, ['permissionAccessZone', 'accessType']).replace(
            '"null"',
            'null',
        );

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userResourcePermissions {
                    resourcePermission {
                        createMultiplePermissions(resourcePermissions: ${createParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.resourcePermission.createMultiplePermissions.data;
                }),
            );
    }

    public updateUserGroupPermissions(userGroupPermissions: Partial<UserGroupPermission>[]): Observable<any> {
        const updateParams = convertObjectArrayToString(userGroupPermissions, ['id', 'accessType'], ['accessType']);

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userGroupPermissions {
                    userGroupPermissions {
                        updateMultipleGroupPermission(userGroupPermissions: ${updateParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroupPermissions.updateMultipleGroupPermission.data;
                }),
            );
    }

    public updateUserPermissions(userPermissions: Partial<UserPermission>[]): Observable<any> {
        const updateParams = convertObjectArrayToString(userPermissions, ['id', 'userId', 'accessType'], ['accessType']);

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userPermissions {
                    userPermissions {
                        updateMultiplePermissions(userPermissions: ${updateParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userPermissions.updateMultiplePermissions.data;
                }),
            );
    }

    public updateResourcePermissions(resourcePermissions: Partial<ResourcePermission>[]): Observable<any> {
        const updateParams = convertObjectArrayToString(resourcePermissions, ['id', 'accessType', 'userId'], ['accessType']).replace(
            '"null"',
            'null',
        );

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userResourcePermissions {
                    resourcePermission {
                        updateMultiplePermissions(resourcePermissions: ${updateParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.resourcePermission.updateMultiplePermissions.data;
                }),
            );
    }

    public updateUserGroupResourcePermissions(resourcePermissions: Partial<ResourcePermission>[]): Observable<any> {
        const updateParams = convertObjectArrayToString(resourcePermissions, ['id', 'accessType', 'userGroupId'], ['accessType']).replace(
            '"null"',
            'null',
        );

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userResourcePermissions {
                    resourcePermission {
                        updateMultipleGroupPermissions(resourcePermissions: ${updateParams}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.resourcePermission.updateMultipleGroupPermissions.data;
                }),
            );
    }

    public deleteUserGroupPermissions(userGroupPermissions: (Partial<UserGroupPermission> | string)[]): Observable<any> {
        const idString: string = this.getIdsParam(userGroupPermissions);

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userGroupPermissions {
                    userGroupPermissions {
                        deleteMultiplePermissions(userGroupPermissionIDs: ${idString}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroupPermissions.deleteMultiplePermissions.data;
                }),
            );
    }

    public deleteUserPermissions(userPermissions: (Partial<UserPermission> | string)[]): Observable<any> {
        const idString: string = this.getIdsParam(userPermissions);
        const userIdString: string = this.getUserId(userPermissions);

        return this.apollo
            .mutate({
                mutation: gql`
                mutation userPermissions {
                    userPermissions {
                        deleteMultiplePermissions(userPermissionIDs: ${idString}, userId:${userIdString}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userPermissions.deleteMultiplePermissions.data;
                }),
            );
    }

    public deleteUserGroupResourcePermissions(resourcePermissions: (Partial<ResourcePermission> | string)[]): Observable<any> {
        const idString: string = this.getIdsParam(resourcePermissions);

        return this.apollo
            .mutate({
                mutation: gql`
                mutation resourcePermissions {
                    resourcePermission {
                        deleteMultipleGroupPermissions(resourcePermissionIDs: ${idString}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.resourcePermission.deleteMultipleGroupPermissions.data;
                }),
            );
    }

    public deleteResourcePermissions(resourcePermissions: (Partial<ResourcePermission> | string)[]): Observable<any> {
        const idString: string = this.getIdsParam(resourcePermissions);
        const userId: string = this.getUserId(resourcePermissions);

        return this.apollo
            .mutate({
                mutation: gql`
                mutation resourcePermissions {
                    resourcePermission {
                        deleteMultiplePermissions(resourcePermissionIDs: ${idString}, userId : ${userId}) {
                            data
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.resourcePermission.deleteMultiplePermissions.data;
                }),
            );
    }

    private getIdsParam(resourcePermissions: (string | Partial<ResourcePermission | UserGroupPermission | UserPermission>)[]): string {
        const ids: string[] = [];

        for (let i = 0; i < resourcePermissions.length; i++) {
            const resourcePermission = resourcePermissions[i];

            if (typeof resourcePermission === 'object' && resourcePermission.id) {
                ids.push(resourcePermission.id);
            } else if (typeof resourcePermission === 'string') {
                ids.push(resourcePermission);
            }
        }

        return ids.length ? `["${ids.join('", "')}"]` : '[]';
    }

    private getUserId(resourcePermissions: (string | Partial<ResourcePermission | UserGroupPermission | UserPermission>)[]): string {
        const userIds = Object.keys(groupBy(resourcePermissions, (x) => (x as any).userId));
        if (!userIds || !userIds[0] || userIds.length > 1) {
            throw 'Invalid argument: userId';
        }
        return `"${userIds[0]}"`;
    }

    public separateResourcePermissionRequests(
        currentPermissions: ApplicationPermissionArray,
        previousPermissions: ApplicationPermissionArray,
        caseForUser: boolean = false,
    ): {
        resourcePermissions: {
            create: Partial<ResourcePermission>[];
            update: Partial<ResourcePermission>[];
            delete: Partial<ResourcePermission>[];
        };
        userGroupPermissions: {
            create: Partial<UserGroupPermission | UserPermission>[];
            update: Partial<UserGroupPermission | UserPermission>[];
            delete: Partial<UserGroupPermission | UserPermission>[];
        };
    } {
        const permissions: { [key: string]: any } = {
            [caseForUser ? 'resourcePermissions' : 'userGroupResourcePermissions']: {
                create: [] as Partial<ResourcePermission>[],
                update: [] as Partial<ResourcePermission>[],
                delete: [] as Partial<ResourcePermission>[],
            },
            [caseForUser ? 'userPermissions' : 'userGroupPermissions']: {
                create: [] as Partial<UserGroupPermission | UserPermission>[],
                update: [] as Partial<UserGroupPermission | UserPermission>[],
                delete: [] as Partial<UserGroupPermission | UserPermission>[],
            },
        };
        const originalPermissionsCopy = previousPermissions.slice();
        const currentPermissionsCopy = currentPermissions.slice();
        let permissionTypes;

        if (caseForUser) {
            permissionTypes = ['resourcePermissions', 'userPermissions'];
        } else {
            permissionTypes = ['userGroupResourcePermissions', 'userGroupPermissions'];
        }

        for (let index = 0; index < permissionTypes.length; index++) {
            const permissionType = permissionTypes[index];

            const currentPermissionAtIndex = currentPermissionsCopy[index].slice();
            const originalPermissionAtIndex = originalPermissionsCopy[index].slice();

            for (let i = 0; i < currentPermissionAtIndex.length; i++) {
                const permission = currentPermissionAtIndex[i];
                const originalPermissionIndex = originalPermissionAtIndex.findIndex((item) => {
                    // @ts-ignore
                    return permission.resourceId === item.resourceId && permission.permissionAccessZone === item.permissionAccessZone;
                });

                if (originalPermissionIndex === -1) {
                    permissions[permissionType].create.push(permission);
                    currentPermissionAtIndex.splice(i, 1);
                    i--;
                    continue;
                }

                if (originalPermissionAtIndex[originalPermissionIndex].accessType !== currentPermissionAtIndex[i].accessType) {
                    permissions[permissionType].update.push(permission);
                }

                originalPermissionAtIndex.splice(originalPermissionIndex, 1);
                currentPermissionAtIndex.splice(i, 1);
                i--;
            }

            for (let i = 0; i < originalPermissionAtIndex.length; i++) {
                const permission = originalPermissionAtIndex[i];

                permissions[permissionType].delete.push(permission);
            }
        }

        return permissions as any;
    }

    public getUserGroupsByGroupId(groupId: string | string[], ignoreCurrentUserGroup: boolean = true): Observable<any> {
        let groupIds = groupId;
        if (!Array.isArray(groupId)) {
            groupIds = [groupId];
        }
        return this.apollo
            .query({
                query: gql`
                query userGroups {
                    userGroups {
                        search(filtering:{ids: [${(groupIds as string[]).map((id) => `"${id}"`)}], ignoreCurrentUserGroup: ${ignoreCurrentUserGroup ? 'true' : 'false'}} ) {
                            data {
                                id
                                supplierId
                                name
                                userAccesses {
                                    userId
                                }
                            },
                            totalCount
                        }
                    }}`,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroups.search.data;
                }),
            );
    }

    public getAllUserGroupsBySupplierIds(
        supplierIds: string | string[],
        ignoreCurrentUserGroup: boolean = true,
    ): Observable<Partial<UserGroup>[]> {
        let sIds = supplierIds;
        if (!Array.isArray(supplierIds)) {
            sIds = [supplierIds];
        }
        return this.apollo
            .query({
                query: gql`
                query userGroups {
                    userGroups {
                        search(filtering:{supplierIds: [${(sIds as string[]).map((id) => `"${id}"`)}], ignoreCurrentUserGroup: ${ignoreCurrentUserGroup ? 'true' : 'false'} } ) {
                            data {
                                id
                                supplierId
                                name
                            },
                            totalCount
                        }
                    }}`,
            })
            .pipe(
                map((response: any) => {
                    return response.data.userGroups.search.data;
                }),
            );
    }
}
